'use client';

import { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { useT } from '@/components/i18n-provider';
import Image from 'next/image';
import {
  Download,
  FileText,
  Music,
  Film,
  Box,
  Image as ImageIcon,
  Share2,
  Send,
  Maximize2,
  Shield,
  ShieldCheck,
  Play,
  Pause,
  Volume2,
  Link2,
  Check,
  Grid2x2Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { ContentViewer } from '@/components/content-viewer';
import { isRenderableDocument } from '@/lib/content/language-map';
import { Tip } from '@/components/ui/tooltip';
import { downloadContent } from '@/lib/content/download-client';
import { MediaLightbox, type MediaLightboxItem } from '@/components/media-lightbox';

// Safari doesn't reliably decode a video frame from preload="metadata" alone.
// Appending #t=0.001 forces the browser to seek, producing a visible poster frame.
function videoThumbnailSrc(src: string): string {
  if (src.includes('#')) return src;
  return src + '#t=0.001';
}

// ========== IMAGE PREVIEW ==========
export const ImagePreview = memo(function ImagePreview({
  src,
  alt,
  className,
  onClick,
  onAddToCanvas,
}: {
  src: string;
  alt?: string;
  className?: string;
  onClick?: () => void;
  /**
   * Optional handler that surfaces this image as a content widget on the
   * workspace canvas. Wired by ChatView when the chat is hosted inside an
   * `AgentNode` (so the canvas is actually mounted) — when undefined we
   * skip the button entirely so standalone chat surfaces stay clean.
   */
  onAddToCanvas?: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className={cn('rounded-lg bg-muted flex items-center justify-center text-muted-foreground', className)}>
        <ImageIcon className="h-8 w-8" />
      </div>
    );
  }

  return (
    // `group/preview` (named) instead of unnamed `group` so the
    // overlay only fires on hover OF THIS image, not on hover of any
    // ancestor that also happens to use `.group` (chat message
    // wrappers, agent-node tabs, etc). The bug it fixes: full-screen
    // chrome appearing over media when the cursor was actually over
    // the prompt input or another sibling.
    <div
      className={cn('relative inline-block rounded-lg overflow-hidden cursor-pointer group/preview', className)}
      style={{ maxWidth: '20rem' }}
      onClick={onClick}
    >
      {!loaded && (
        <Skeleton className="w-full rounded-lg" style={{ aspectRatio: '4/3' }} />
      )}
      <Image
        src={src}
        alt={alt || 'Image'}
        width={600}
        height={400}
        unoptimized
        className={cn(
          'rounded-lg transition-opacity duration-300',
          loaded ? 'max-w-full h-auto opacity-100' : 'absolute inset-0 w-full h-full object-cover opacity-0',
        )}
        sizes="(max-width: 640px) 100vw, 600px"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      <div className="absolute inset-0 bg-black/0 group-hover/preview:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover/preview:opacity-100 pointer-events-none">
        <Maximize2 className="h-6 w-6 text-white drop-shadow-md" />
      </div>
      {onAddToCanvas && (
        <AddToCanvasButton
          onClick={(e) => {
            e.stopPropagation();
            onAddToCanvas();
          }}
        />
      )}
    </div>
  );
}, (prev, next) =>
  prev.src === next.src &&
  prev.alt === next.alt &&
  prev.className === next.className &&
  prev.onAddToCanvas === next.onAddToCanvas,
);

/**
 * Liquid-glass "add to canvas" pill rendered in the upper-right corner
 * of media previews. Visible only when the user hovers the media
 * (scoped to `group/preview` so adjacent UI doesn't trigger it).
 *
 * Stops propagation so clicking it doesn't also open the lightbox.
 */
function AddToCanvasButton({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  const t = useT();
  return (
    <Tip label={t('workspace.canvas.actions.addToCanvas')} side="left">
      <button
        type="button"
        aria-label={t('workspace.canvas.actions.addToCanvas')}
        onClick={onClick}
        className="absolute top-1.5 right-1.5 inline-flex items-center justify-center h-7 w-7 rounded-full bg-black/55 backdrop-blur-md text-white opacity-0 group-hover/preview:opacity-100 transition-opacity ring-1 ring-white/20 shadow-sm hover:bg-black/70"
      >
        <Grid2x2Plus className="h-3.5 w-3.5" />
      </button>
    </Tip>
  );
}

// ========== VIDEO PREVIEW ==========
export const VideoPreview = memo(function VideoPreview({
  src,
  className,
  onClick,
  onAddToCanvas,
  onNaturalSize,
}: {
  src: string;
  className?: string;
  onClick?: () => void;
  onAddToCanvas?: () => void;
  /**
   * Fires once the video reports intrinsic dimensions via
   * `loadedmetadata`. Used by the workspace's headerless content
   * node to record `state.cache.aspectRatio` and reflow the
   * spawned node bounds to match the source video — same trick
   * the image branch in `ContentPreviewBody` uses with
   * `<img onLoad>`. Optional.
   */
  onNaturalSize?: (width: number, height: number) => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className={cn('rounded-lg bg-muted flex items-center justify-center text-muted-foreground', className)} style={{ aspectRatio: '16/9' }}>
        <Film className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div
      className={cn('relative inline-block rounded-lg overflow-hidden cursor-pointer group/preview', className)}
      onClick={onClick}
    >
      {!loaded && (
        <Skeleton className="w-64 rounded-lg flex items-center justify-center" style={{ aspectRatio: '16/9' }}>
          <div className="h-12 w-12 rounded-full bg-muted-foreground/10 flex items-center justify-center">
            <Play className="h-6 w-6 text-muted-foreground/30 ml-0.5" />
          </div>
        </Skeleton>
      )}
      <video
        src={videoThumbnailSrc(src)}
        className={cn(
          'rounded-lg transition-opacity duration-300',
          loaded ? 'max-w-full h-auto opacity-100' : 'absolute inset-0 w-full h-full object-cover opacity-0',
        )}
        preload="metadata"
        muted
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          if (onNaturalSize && v.videoWidth > 0 && v.videoHeight > 0) {
            onNaturalSize(v.videoWidth, v.videoHeight);
          }
        }}
        onLoadedData={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      {loaded && (
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
          <div className="h-12 w-12 rounded-full bg-black/50 flex items-center justify-center">
            <Play className="h-6 w-6 text-white ml-0.5" />
          </div>
        </div>
      )}
      {onAddToCanvas && (
        <AddToCanvasButton
          onClick={(e) => {
            e.stopPropagation();
            onAddToCanvas();
          }}
        />
      )}
    </div>
  );
}, (prev, next) =>
  prev.src === next.src &&
  prev.className === next.className &&
  prev.onAddToCanvas === next.onAddToCanvas,
);

// ========== AUDIO PREVIEW ==========
export const AudioPreview = memo(function AudioPreview({
  src,
  format,
  duration,
  className,
}: {
  src: string | { url: string };
  format?: string;
  duration?: number;
  className?: string;
}) {
  const resolvedSrc = typeof src === 'string' ? src : src.url;
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      // Ensure AudioContext is resumed (browser autoplay policy)
      audioRef.current.play().catch(() => {
        // Retry after user gesture context
      });
    }
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const dur = audioRef.current.duration;
    const cur = audioRef.current.currentTime;
    setCurrentTime(cur);
    setProgress(dur > 0 ? (cur / dur) * 100 : 0);
  };

  const handleEnded = () => {
    setPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = pct * audioRef.current.duration;
  };

  const formatTime = (secs: number) => {
    if (!isFinite(secs) || secs < 0) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const [metaLoaded, setMetaLoaded] = useState(false);

  const audioSrc = resolvedSrc.startsWith('data:') || resolvedSrc.startsWith('http') || resolvedSrc.startsWith('/')
    ? resolvedSrc
    : `data:audio/${format || 'wav'};base64,${resolvedSrc}`;

  const handleMeta = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
    setMetaLoaded(true);
  };

  if (!metaLoaded) {
    return (
      <div className={cn('relative w-[220px] max-w-full', className)}>
        <Skeleton className="h-[36px] w-full rounded-full" />
        <audio src={audioSrc} preload="metadata" onLoadedMetadata={handleMeta} className="hidden" ref={audioRef} />
      </div>
    );
  }

  return (
    <div className={cn('inline-flex items-center gap-2 px-2.5 py-1.5 bg-muted/50 rounded-full w-[220px] max-w-full', className)}>
      <audio
        ref={audioRef}
        src={audioSrc}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onLoadedMetadata={handleMeta}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <button
        onClick={toggle}
        className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 hover:bg-primary/90 transition-colors"
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <div
          ref={progressRef}
          className="flex-1 h-1 bg-muted rounded-full overflow-hidden cursor-pointer"
          onClick={handleSeek}
        >
          <div
            className="h-full bg-primary rounded-full transition-[width] duration-75"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-[10px] text-muted-foreground tabular-nums shrink-0 w-[32px] text-right">
          {playing ? formatTime(currentTime) : (audioDuration > 0 ? formatTime(audioDuration) : '0:00')}
        </span>
      </div>
    </div>
  );
}, (prev, next) => {
  const prevSrc = typeof prev.src === 'string' ? prev.src : prev.src.url;
  const nextSrc = typeof next.src === 'string' ? next.src : next.src.url;
  return prevSrc === nextSrc && prev.format === next.format && prev.className === next.className;
});

// ========== DOCUMENT PREVIEW ==========
export function DocumentPreview({
  url,
  filename,
  mimeType,
  className,
  onClick,
}: {
  url: string;
  filename: string;
  mimeType?: string;
  className?: string;
  onClick?: () => void;
}) {
  const parts = filename.split('.');
  const ext = parts.length > 1
    ? (parts.pop()?.toUpperCase() || 'FILE')
    : (mimeType?.split('/').pop()?.toUpperCase() || 'FILE');

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 bg-muted/50 rounded-xl cursor-pointer hover:bg-muted/80 transition-colors',
        className
      )}
      onClick={onClick}
    >
      <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
        <FileText className="h-5 w-5 text-blue-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{filename}</p>
        <p className="text-xs text-muted-foreground">{ext}</p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); downloadContent({ url, filename, mimeType }); }}
        className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-accent text-muted-foreground shrink-0"
      >
        <Download className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Max pointer movement (px) to still count as a click vs a drag on the 3D preview. */
const MODEL3D_CLICK_MAX_MOVE_PX = 10;

// ========== 3D MODEL PREVIEW ==========
export const Model3DPreview = memo(function Model3DPreview({
  src,
  className,
  onClick,
}: {
  src: string;
  className?: string;
  onClick?: () => void;
}) {
  const t = useT();
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!onClick || e.button !== 0) return;
      pointerStartRef.current = { x: e.clientX, y: e.clientY };
    },
    [onClick],
  );

  const clearPointerStart = useCallback(() => {
    pointerStartRef.current = null;
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!onClick || e.button !== 0 || !pointerStartRef.current) return;
      const start = pointerStartRef.current;
      pointerStartRef.current = null;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.hypot(dx, dy) < MODEL3D_CLICK_MAX_MOVE_PX) {
        onClick();
      }
    },
    [onClick],
  );

  if (!src) {
    return (
      <div
        className={cn(
          'rounded-lg bg-gradient-to-br from-purple-500/10 to-blue-500/10 flex items-center justify-center',
          className,
        )}
        style={{ minHeight: 120 }}
      >
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <Box className="h-10 w-10" />
          <span className="text-xs font-medium">3D Model</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn('relative w-full overflow-hidden rounded-lg', className)}
      onPointerDownCapture={onClick ? handlePointerDown : undefined}
      onPointerUpCapture={onClick ? handlePointerUp : undefined}
      onPointerCancelCapture={onClick ? clearPointerStart : undefined}
    >
      {/* @ts-expect-error model-viewer is a web component */}
      <model-viewer
        src={src}
        alt={t('contentPreview.model3d')}
        auto-rotate
        camera-controls
        loading="lazy"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      />
    </div>
  );
}, (prev, next) => prev.src === next.src && prev.className === next.className);

// ========== FILE CARD (generic fallback) ==========
export function FileCard({
  url,
  filename,
  mimeType,
  size,
  className,
}: {
  url: string;
  filename: string;
  mimeType?: string;
  size?: number;
  className?: string;
}) {
  const getIcon = () => {
    if (mimeType?.startsWith('image/')) return <ImageIcon className="h-5 w-5" />;
    if (mimeType?.startsWith('video/')) return <Film className="h-5 w-5" />;
    if (mimeType?.startsWith('audio/')) return <Music className="h-5 w-5" />;
    if (mimeType?.includes('gltf') || mimeType?.includes('usdz')) return <Box className="h-5 w-5" />;
    return <FileText className="h-5 w-5" />;
  };

  return (
    <button
      onClick={() => downloadContent({ url, filename, mimeType })}
      className={cn(
        'flex items-center gap-3 p-3 bg-muted/50 rounded-xl hover:bg-muted/80 transition-colors w-full text-left',
        className
      )}
    >
      <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{filename}</p>
        {size && <p className="text-xs text-muted-foreground">{formatBytes(size)}</p>}
      </div>
      <Download className="h-4 w-4 text-muted-foreground shrink-0" />
    </button>
  );
}

// ========== C2PA BADGE ==========
export function C2PABadge({
  contentId,
  className,
}: {
  contentId: string;
  className?: string;
}) {
  const [manifest, setManifest] = useState<Record<string, unknown> | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    fetch(`/api/content/${contentId}/c2pa`)
      .then((r) => r.json())
      .then((data) => {
        setChecked(true);
        if (data.hasCredentials) setManifest(data);
      })
      .catch(() => setChecked(true));
  }, [contentId]);

  if (!checked || !manifest) return null;

  return (
    <Tip label={`Producer: ${manifest.producer || 'Unknown'}`}>
    <div
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium',
        manifest.isAiGenerated
          ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
          : 'bg-green-500/10 text-green-600 dark:text-green-400',
        className
      )}
    >
      {manifest.isAiGenerated ? (
        <Shield className="h-3 w-3" />
      ) : (
        <ShieldCheck className="h-3 w-3" />
      )}
      <span>C2PA {manifest.isAiGenerated ? 'AI' : 'Verified'}</span>
    </div>
    </Tip>
  );
}

// ========== LIGHTBOX ==========
export interface LightboxItem {
  url: string;
  type: 'image' | 'video' | 'audio' | 'document' | 'model3d';
  filename?: string;
  mimeType?: string;
  contentId?: string;
}

export function Lightbox({
  items,
  initialIndex = 0,
  onClose,
  onShareToAgent,
}: {
  items: LightboxItem[];
  initialIndex?: number;
  onClose: () => void;
  onShareToAgent?: (contentId: string) => void;
}) {
  const t = useT();
  const [index, setIndex] = useState(initialIndex);
  const [linkCopied, setLinkCopied] = useState(false);

  const mediaItems = useMemo<MediaLightboxItem[]>(
    () =>
      items.map((it) => ({
        url: it.url,
        type: it.type,
        contentId: it.contentId,
        mimeType: it.mimeType,
        filename: it.filename,
      })),
    [items],
  );

  const item = items[index];
  if (!item) return null;

  const headerLeft = (
    <div className="flex items-center gap-3 min-w-0">
      <span className="text-sm font-medium text-white/80 truncate">
        {item.filename || `${item.type} ${index + 1}`}
      </span>
      {item.contentId && <C2PABadge contentId={item.contentId} />}
    </div>
  );

  const headerActions = (
    <>
      {item.contentId && (
        <Tip label={linkCopied ? t('settings.copied') : t('posts.copyLink')}>
          <button
            onClick={async () => {
              const baseUrl = window.location.origin;
              const publicUrl = `${baseUrl}/api/content/${item.contentId}`;
              await navigator.clipboard.writeText(publicUrl);
              setLinkCopied(true);
              setTimeout(() => setLinkCopied(false), 2000);
            }}
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10 text-white/60 transition-colors"
            aria-label={t('posts.copyLink')}
          >
            {linkCopied ? <Check className="h-4 w-4 text-green-400" /> : <Link2 className="h-4 w-4" />}
          </button>
        </Tip>
      )}
      {onShareToAgent && item.contentId && (
        <Tip label={t('contentPreview.shareToAgent')}>
          <button
            onClick={() => onShareToAgent(item.contentId!)}
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10 text-white/60 transition-colors"
            aria-label={t('contentPreview.shareToAgent')}
          >
            <Send className="h-4 w-4" />
          </button>
        </Tip>
      )}
      <Tip label={t('content.download')}>
        <button
          onClick={() =>
            downloadContent({
              contentId: item.contentId,
              url: item.url,
              filename: item.filename,
              mimeType: item.mimeType,
            })
          }
          className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10 text-white/60 transition-colors"
          aria-label={t('content.download')}
        >
          <Download className="h-4 w-4" />
        </button>
      </Tip>
    </>
  );

  // Custom body for the document type so we keep ContentViewer / DocumentPreview;
  // returning null/undefined for other types lets MediaLightbox fall back to its
  // default renderers (image / video / audio / model3d).
  const renderBody = (mi: MediaLightboxItem) => {
    if (mi.type !== 'document') return null;
    if (mi.mimeType && isRenderableDocument(mi.mimeType)) {
      return (
        <div className="w-full max-w-3xl h-full max-h-[80vh] bg-background rounded-lg shadow-lg">
          <ContentViewer
            contentUrl={mi.url}
            mimeType={mi.mimeType}
            filename={mi.filename}
            maxHeight="80vh"
            className="rounded-lg overflow-hidden"
            contentId={mi.contentId}
          />
        </div>
      );
    }
    return (
      <div className="w-full max-w-md">
        <DocumentPreview
          url={mi.url}
          filename={mi.filename || 'Document'}
          mimeType={mi.mimeType}
          className="bg-white/10"
        />
      </div>
    );
  };

  return (
    <MediaLightbox
      items={mediaItems}
      initialIndex={initialIndex}
      onClose={onClose}
      onIndexChange={setIndex}
      headerLeft={headerLeft}
      headerActions={headerActions}
      renderBody={renderBody}
    />
  );
}

// ========== HELPERS ==========
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
